import React, { useState, useMemo } from "react";
import { ResponsiveContainer, Treemap, Tooltip, Cell } from "recharts";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length > 0) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border rounded shadow-sm p-2 text-sm">
        <strong>{data.name}</strong>
        {data.archdeaconry_name && <div>Archdeaconry: {data.archdeaconry_name}</div>}
        {data.parish_name && <div>Parish: {data.parish_name}</div>}
        {data.type === "Both" ? (
          <>
            <div>Attendance: {data.total_attendance?.toLocaleString?.() ?? 0}</div>
            <div>Collections: Ksh {data.total_collection?.toLocaleString?.() ?? 0}</div>
          </>
        ) : (
          <div>
            {data.type === "Attendance"
              ? `Attendance: ${data.size?.toLocaleString?.() ?? data.size}`
              : `Collections: Ksh ${data.size?.toLocaleString?.() ?? data.size}`}
          </div>
        )}
      </div>
    );
  }
  return null;
};

const HierarchyChart = ({ data, filters }) => {
  const [expandedArchs, setExpandedArchs] = useState(new Set());

  const attendanceHierarchy = data?.hierarchy_attendance || [];
  const collectionHierarchy = data?.hierarchy_collections || [];

  // ✅ Combine both datasets when needed
  const selectedHierarchy = useMemo(() => {
    if (filters.data_type === "attendance") return attendanceHierarchy;
    if (filters.data_type === "collections") return collectionHierarchy;

    // Merge both into combined view
    const merged = {};
    attendanceHierarchy.forEach((a) => {
      const key = `${a.archdeaconry_name}-${a.parish_name}-${a.congregation_name}`;
      merged[key] = { ...a, total_collection: 0 };
    });
    collectionHierarchy.forEach((c) => {
      const key = `${c.archdeaconry_name}-${c.parish_name}-${c.congregation_name}`;
      if (merged[key]) merged[key].total_collection = c.total_collection || 0;
      else merged[key] = { ...c, total_attendance: 0 };
    });
    return Object.values(merged);
  }, [attendanceHierarchy, collectionHierarchy, filters.data_type]);

  // ✅ Build nested structure (arch → parish → congregation)
  const nestedData = useMemo(() => {
    const tree = {};
    selectedHierarchy.forEach((item) => {
      const arch = item.archdeaconry_name || "Unknown Archdeaconry";
      const parish = item.parish_name || "Unknown Parish";
      const congregation = item.congregation_name || "Unknown Congregation";
      const att = item.total_attendance ?? 0;
      const col = item.total_collection ?? 0;
      const total = att + col;

      if (!tree[arch])
        tree[arch] = { name: arch, children: {}, total_attendance: 0, total_collection: 0 };
      if (!tree[arch].children[parish])
        tree[arch].children[parish] = { name: parish, children: {}, total_attendance: 0, total_collection: 0 };

      tree[arch].children[parish].children[congregation] = {
        name: congregation,
        size: filters.data_type === "attendance" ? att : filters.data_type === "collections" ? col : total,
        archdeaconry_name: arch,
        parish_name: parish,
        total_attendance: att,
        total_collection: col,
        type:
          filters.data_type === "attendance"
            ? "Attendance"
            : filters.data_type === "collections"
            ? "Collection"
            : "Both",
      };

      tree[arch].children[parish].total_attendance += att;
      tree[arch].children[parish].total_collection += col;
      tree[arch].total_attendance += att;
      tree[arch].total_collection += col;
    });

    return Object.values(tree).map((arch) => {
      const total = arch.total_attendance + arch.total_collection;
      return {
        name: arch.name,
        size:
          filters.data_type === "attendance"
            ? arch.total_attendance
            : filters.data_type === "collections"
            ? arch.total_collection
            : total,
        total_attendance: arch.total_attendance,
        total_collection: arch.total_collection,
        type:
          filters.data_type === "attendance"
            ? "Attendance"
            : filters.data_type === "collections"
            ? "Collection"
            : "Both",
        children: expandedArchs.has(arch.name)
          ? Object.values(arch.children).map((parish) => ({
              name: parish.name,
              size:
                filters.data_type === "attendance"
                  ? parish.total_attendance
                  : filters.data_type === "collections"
                  ? parish.total_collection
                  : parish.total_attendance + parish.total_collection,
              children: Object.values(parish.children),
            }))
          : undefined,
      };
    });
  }, [selectedHierarchy, filters.data_type, expandedArchs]);

  const handleCellClick = (entry) => {
    if (!entry?.name) return;
    setExpandedArchs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entry.name)) newSet.delete(entry.name);
      else newSet.add(entry.name);
      return newSet;
    });
  };

  const getColor = (type) => {
    if (type === "Attendance") return "#82ca9d";
    if (type === "Collection") return "#8884d8";
    return "#6baed6";
  };

  return (
    <div className="card shadow-sm h-100">
      <div className="card-body">
        <h5 className="card-title border-bottom pb-2">
          {filters.data_type === "attendance"
            ? "Attendance Hierarchy"
            : filters.data_type === "collections"
            ? "Collections Hierarchy"
            : "Attendance & Collections Hierarchy"}
        </h5>

        {nestedData.length > 0 ? (
          <ResponsiveContainer width="100%" height={450}>
            <Treemap
              data={nestedData}
              dataKey="size"
              stroke="#fff"
              isAnimationActive={false}
              nameKey="name"
              onClick={handleCellClick}
            >
              {nestedData.flatMap((entry, index) =>
                (entry.children || [entry]).map((child, i) => (
                  <Cell
                    key={`cell-${index}-${i}`}
                    fill={getColor(child.type || entry.type)}
                    stroke="#fff"
                    cursor="pointer"
                  />
                ))
              )}
              <Tooltip content={<CustomTooltip />} />
            </Treemap>
          </ResponsiveContainer>
        ) : (
          <p className="text-muted text-center my-4">
            No hierarchy data available for the selected filter.
          </p>
        )}
      </div>
    </div>
  );
};

export default HierarchyChart;
